import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { EmailDetail, MessageView } from "@/lib/types";
import MessageBubble, { messageStamp } from "@/components/MessageBubble";

/**
 * The rest of the thread a message belongs to, shown under that message.
 *
 * <p>The oversight list opens one message at a time, but a message is only
 * judged against what it answers: a reply that reads as curt is a fair reply
 * to a curt question. This lays out every other message of the same thread,
 * newest first, each a one-line summary that opens to its full body. Asking
 * for a message's review controls hands it to the caller, which puts it in the
 * reviewer above — and this history rebuilds itself around it.
 */
export default function ThreadHistory({
  emailId,
  onSelect,
}: {
  emailId: string;
  /** The operator wants this message in the reviewer instead. */
  onSelect: (id: string) => void;
}) {
  // The same query the reviewer runs, so opening a message costs one fetch.
  const detail = useQuery({
    queryKey: ["email", emailId],
    queryFn: () => api.get<EmailDetail>(`/api/v1/emails/${emailId}`),
  });
  const threadId = detail.data?.threadId ?? null;

  const messages = useQuery({
    queryKey: ["thread-messages", threadId],
    queryFn: () => api.get<MessageView[]>(`/api/v1/threads/${threadId}/messages`),
    enabled: !!threadId,
  });

  // What the operator unrolled belongs to the message they unrolled it under.
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  useEffect(() => {
    setExpandedIds([]);
  }, [emailId]);

  if (!threadId || messages.isLoading) {
    return (
      <div className="mt-3 border-t border-[var(--border)] pt-3 text-sm text-zinc-500">
        Loading thread…
      </div>
    );
  }
  if (messages.isError) {
    return (
      <div className="mt-3 border-t border-[var(--border)] pt-3 text-sm text-red-700">
        The rest of this thread could not be loaded.
      </div>
    );
  }

  const rows = (messages.data ?? [])
    .filter((m) => m.id !== emailId)
    .sort((a, b) => messageStamp(b).localeCompare(messageStamp(a)));

  return (
    <section
      aria-label="Earlier in this thread"
      className="mt-3 border-t border-[var(--border)] pt-3"
    >
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
        Earlier in this thread
        {rows.length > 0 ? (
          <span className="ml-2 font-normal normal-case tracking-normal">
            {rows.length} earlier message{rows.length === 1 ? "" : "s"}
          </span>
        ) : null}
      </h3>
      {rows.length === 0 ? (
        <div className="text-sm text-[var(--fg-muted)]">
          This is the first message in this thread.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((m) => (
            <MessageBubble
              key={m.id}
              m={m}
              state={expandedIds.includes(m.id) ? "body" : "collapsed"}
              onExpand={() => setExpandedIds((ids) => [...ids, m.id])}
              onToggleReview={() => onSelect(m.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
